import { ComponentPropsWithoutRef } from 'react'

import { useRouter } from 'next/router'

import useSWR from 'swr'

import { ArticleFeedCarouselDocument, ArticleFilter, ArticleOrder } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { serializeCacheKey } from '@/lib/contentful/utils'

import { CardCarousel } from '@/components/CardCarousel'

type FeedType = 'latest' | 'popular' | 'root'

type BaseProps = {
	type?: FeedType
	ctaText?: string
}

type Args = {
	type: FeedType
	preview?: boolean
}

export function getArticleParams({ type, preview }: Args): {
	where?: ArticleFilter
	order?: ArticleOrder
	limit?: number
	skip?: number
	preview?: boolean
} {
	switch (type) {
		case 'latest': {
			return {
				where: {
					body_exists: true,
					featuredImage_exists: true,
					editStatus: '🟢 live'
				},
				order: ArticleOrder.PublishedDateDesc,
				skip: 0,
				preview: Boolean(preview)
			}
		}
		case 'root': {
			return {
				where: {
					parentArticle_exists: false,
					body_exists: true,
					featuredImage_exists: true,
					editStatus: '🟢 live'
				},
				skip: 0,
				preview: Boolean(preview)
			}
		}
		case 'popular': {
			return {
				where: {
					popular: true,
					parentArticle_exists: true,
					body_exists: true,
					featuredImage_exists: true,
					editStatus: '🟢 live'
				},
				order: ArticleOrder.PublishedDateDesc,
				skip: 0,
				preview: Boolean(preview)
			}
		}
	}
}

type Props = BaseProps &
	Omit<ComponentPropsWithoutRef<typeof CardCarousel>, keyof BaseProps | 'cards' | ''>

export function ArticleFeedCarousel({ type = 'root', ctaText, ...rest }: Props) {
	const { locale, isPreview } = useRouter()

	const { data } = useSWR(serializeCacheKey('articleFeed', { type, locale }), () =>
		client({ preview: Boolean(isPreview) }).request(ArticleFeedCarouselDocument, {
			...getArticleParams({ type, preview: isPreview }),
			locale
		})
	)

	if (!data?.articleCollection?.items) return null

	return (
		<CardCarousel
			{...rest}
			dataTestId="article-feed-carousel"
			cards={data.articleCollection.items.map((item) => {
				const isRoot = !item?.parentArticle?.slug
				const href = isRoot
					? `/articles/${item?.slug}`
					: `/articles/${item?.parentArticle?.slug}/${item?.slug}`
				return {
					heading: 'articles',
					linkText: ctaText || 'Read article',
					imageAlt: item?.featuredImage?.title ?? `${item?.title} image`,
					imageSrc: {
						url: item?.featuredImage?.url ?? '',
						dimensions: {
							width: item?.featuredImage?.width ?? 200,
							height: item?.featuredImage?.height ?? 200
						}
					},
					title: item?.title ?? '',
					text: item?.cardSummary ?? item?.seoMetadata?.description ?? '',
					linkURL: { href: href, target: '_self' }
				}
			})}
		/>
	)
}
